import {
  contactsDestroyMutation,
  contactsListOptions,
  contactsListQueryKey,
  ReturnContactReadable
} from '@crm/api'
import { ConfirmModal, Leads, PhoneNumbers } from '@crm/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form } from 'antd'
import { Add, Edit2, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import { ContactDetailsModal, ContactFilters, ContactForm } from '../components'

import type { MenuProps, TableColumnsType } from 'antd'

export const Contacts = () => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()

  const { message } = App.useApp()
  const [isModalContactDetailsOpen, setIsModalContactDetailsOpen] =
    useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editIdItem, setEditIdItem] = useState<number>()
  const [deleteIdItem, setDeleteIdItem] = useState<number>()

  const { data: contacts, isLoading } = useQuery(
    contactsListOptions({
      query: params
    })
  )

  const { mutate: deleteMutation } = useMutation({
    ...contactsDestroyMutation(),
    onSuccess: () => {
      invalidateQueries([contactsListQueryKey()])
      resetPagination()
      setDeleteIdItem(undefined)
      message.success(t('success.success_data_delete'))
    }
  })

  const onCloseContactForm = () => {
    setIsModalOpen(false)
    setEditIdItem(undefined)
    form.resetFields()
  }

  const onToggle = () => setIsModalOpen((prev) => !prev)
  const onToggleContactDetailsModal = () => {
    setIsModalContactDetailsOpen((prev) => !prev)
    setEditIdItem(undefined)
  }

  const onEdit = (id: number) => setEditIdItem(id)
  const onPreview = useCallback((id: number) => {
    onToggleContactDetailsModal()
    onEdit(Number(id))
  }, [])

  const onRow = (record: ReturnContactReadable) => ({
    onClick: () => onPreview(record.id)
  })

  const onDelete = () => {
    if (deleteIdItem)
      deleteMutation({
        path: {
          id: deleteIdItem
        }
      })
  }

  const getActionColumnItem: (
    contact: ReturnContactReadable
  ) => MenuProps['items'] = useCallback(
    (contact) => [
      {
        label: t('documents.edit'),
        key: 'edit',
        icon: <Edit2 size={20} />,
        onClick: () => onEdit(Number(contact?.id))
      },
      { type: 'divider' },
      {
        label: t('documents.delete'),
        key: 'delete',
        danger: true,
        icon: <Trash size={20} />,
        onClick: () => setDeleteIdItem(Number(contact?.id))
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnContactReadable> = useMemo(
    () => [
      {
        title: t('contacts.full_name'),
        key: 'first_name',
        sorter: true,
        render: (_, record) => (
          <ConditionalText
            condition={!!record?.first_name || !!record?.last_name}
          >
            {getFullName(record)}
          </ConditionalText>
        )
      },
      {
        title: t('contacts.email'),
        key: 'email',
        sorter: true,
        render: (_, { email, email_valid }) => (
          <ConditionalText
            condition={!!email}
            type={email_valid ? undefined : 'danger'}
          >
            {email}
          </ConditionalText>
        )
      },
      {
        title: t('contacts.phone_number'),
        key: 'phone_number',
        sorter: true,
        render: (_, record) => (
          <PhoneNumbers
            onClick={(e) => e.stopPropagation()}
            phoneNumbers={record?.phone_numbers}
          />
        )
      },
      {
        title: t('lead.leads'),
        key: 'lead',
        sorter: true,
        width: 150,
        render: (_, record) => (
          <Leads onClick={(e) => e.stopPropagation()} leads={record.leads} />
        )
      },
      {
        title: t('task.created_by'),
        key: 'created_by',
        sorter: true,
        render: (_, { created_by }) => (
          <ConditionalText condition={!!created_by}>
            {`${created_by?.first_name} ${created_by?.last_name}`}
          </ConditionalText>
        )
      },
      {
        title: t('lead.created_at'),
        key: 'created_at',
        sorter: true,
        render: (_, { created_at }) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        key: 'modified_at',
        sorter: true,
        render: (_, { modified_at }) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        key: 'actions',
        fixed: 'right',
        render: (contact) => (
          <TableActions options={getActionColumnItem(contact)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <ContactForm
        form={form}
        id={editIdItem}
        open={!isModalContactDetailsOpen && (isModalOpen || !!editIdItem)}
        onClose={onCloseContactForm}
      />

      <ContactDetailsModal
        id={editIdItem}
        open={isModalContactDetailsOpen}
        onCancel={onToggleContactDetailsModal}
      />

      <ConfirmModal
        open={!!deleteIdItem}
        onSuccess={onDelete}
        onCancel={() => setDeleteIdItem(undefined)}
      />

      <TitleSection
        title={t(`contacts.contacts`)}
        count={contacts?.count}
        isLoading={isLoading}
      />

      <TableContainer
        data={contacts}
        columns={columns}
        isLoading={isLoading}
        isHoverAlternative
        rowKey="id"
        onRow={onRow}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        leftChildren={<ContactFilters />}
        rightChildren={
          <Button type="primary" onClick={onToggle} icon={<Add size={16} />}>
            {t('contacts.add_contact')}
          </Button>
        }
      />
    </>
  )
}
